import React from 'react'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { Box, Grid, Typography } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { EditTariffFormData } from '../../models/tariffs-form-model'
import { currencyCodeToSymbol } from 'src/Libs/e-mobility/shared'
import { Controller, useFormContext } from 'react-hook-form'
import { K1Information } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  alertContainer: {
    padding: theme.spacing(2, 3),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.tariff.status.inactive.background,
    display: 'flex',
    alignItems: 'center',
  },
  alertText: {
    color: theme.palette.grey.grey01,
    fontSize: '14px',
    lineHeight: '18px',
    textAlign: 'center',
  },
}))

/**
 * Component with form fields for edit dynamic tariff
 * It requires formik context for the input fields
 */
export const EditDynamicTariffForm = (): JSX.Element => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<EditTariffFormData>()
  const { t } = useCustomTranslation()
  const currencySymbol = currencyCodeToSymbol(watch('currencyCode'))
  const { classes } = useStyles()

  return (
    <Grid container spacing={5} my={3}>
      <Grid item xs={12}>
        <Controller
          name="tariffName"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={t('emobility.tariffs.tariffName')}
              helperText={errors.tariffName?.message}
              required
              clearable={false}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Box className={classes.alertContainer}>
          <K1Information />
          <Box width="100%">
            <Typography className={classes.alertText}>{t('emobility.tariffs.dynamic-tariff-info')}</Typography>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="tariffSolarPrice"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              value={value ?? undefined}
              /**
               * In case of editing a dynamic tariff, solar price must be defined, even as a zero. This is different compared to editing static
               * tariff where we can simply ignore the solar price component of type "FLAT", therefore there is no need to explicitly convert the
               * value returned from "onChange" event to null and store in the form as such compared to the dischared price.
               */
              onChange={onChange}
              onBlur={onBlur}
              type="number"
              step={0.1}
              label={t('emobility.tariffs.solar-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              helperText={errors.tariffSolarPrice?.message}
              clearable={false}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="tariffDischargePrice"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              value={value ?? undefined}
              onChange={onChange}
              onBlur={onBlur}
              type="number"
              step={0.1}
              label={t('emobility.tariffs.discharge-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              helperText={errors.tariffDischargePrice?.message}
              clearable={false}
            />
          )}
        />
      </Grid>
    </Grid>
  )
}
